/**
 * SyncEmployeesModal — pull pracowników z Symfonia FK (FK-only).
 *
 * Modal dwuetapowy (spójny z importem/eksportem kontrahentów):
 *  1. Wybór trybu — full (pełen sync) / incremental (od daty).
 *  2. Progress — polling job status + wspólny widok wyniku `SyncResultPanel`
 *     (pigułki klikalne + tabela rekordów z drill-downem „co się zmieniło").
 *
 * POST /api/erp-integrations/:id/sync-employees. Timeout UI 5 min.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { RadioGroup } from '@/components/RadioGroup';
import { api } from '@/lib/api';

import { isJobInFlight } from './job-in-flight';
import { type SyncRecordDetail, SyncResultPanel, type SyncResultTile } from './SyncResultPanel';
import type { JobDto } from './TestConnectionModal';

type Stage = 'choose' | 'progress';
type Mode = 'full' | 'incremental';

interface Props {
  open: boolean;
  integrationId: string | null;
  integrationName: string;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60_000;

interface SyncStats {
  added?: number;
  updated?: number;
  unchanged?: number;
  skipped?: number;
  inactive?: number;
  excluded?: number;
}

interface SyncResultPayload {
  outcome?: 'ok' | 'partial' | 'no_op';
  stats?: SyncStats;
  warnings?: string[];
  /** Listy rekordów per kubełek (drill-down w pigułkach). */
  details?: Record<string, SyncRecordDetail[]>;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function dateMinus30ISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

export function SyncEmployeesModal({ open, integrationId, integrationName, onClose }: Props) {
  const { t } = useTranslation();
  const [stage, setStage] = useState<Stage>('choose');
  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState<Mode>('full');
  const [dateFrom, setDateFrom] = useState<string>(dateMinus30ISO());
  const [jobId, setJobId] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!open) {
      setStage('choose');
      setExpanded(false);
      setMode('full');
      setJobId(null);
      setTimedOut(false);
      return;
    }
    if (stage !== 'progress' || !jobId) return;
    const timer = window.setTimeout(() => setTimedOut(true), POLL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [open, stage, jobId]);

  const startSyncMutation = useMutation({
    mutationFn: async () => {
      if (!integrationId) throw new Error('No integrationId');
      const body: { mode: Mode; dateFrom?: string } = { mode };
      if (mode === 'incremental') body.dateFrom = dateFrom;
      const res = await api.post<{ jobId: string }>(
        `/erp-integrations/${integrationId}/sync-employees`,
        body,
      );
      return res.data.jobId;
    },
    onSuccess: (id) => {
      setJobId(id);
      setStage('progress');
    },
  });

  const jobQ = useQuery({
    queryKey: ['erp-sync-job', jobId],
    queryFn: async () => {
      const res = await api.get<{ job: JobDto }>(`/erp-sync-jobs/${jobId}`);
      return res.data.job;
    },
    enabled: !!jobId && open && !timedOut && stage === 'progress',
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return POLL_INTERVAL_MS;
    },
  });

  if (!open) return null;

  const job = jobQ.data ?? null;
  const result = (job?.result ?? null) as SyncResultPayload | null;

  const P = 'tenant.settings.integrations.syncEmployees.stats';
  const d = result?.details ?? {};
  const s = result?.stats ?? {};
  const resultTiles: SyncResultTile[] = [
    {
      key: 'added',
      label: t(`${P}.added`),
      count: s.added ?? 0,
      tone: 'added',
      records: d.added ?? [],
    },
    {
      key: 'updated',
      label: t(`${P}.updated`),
      count: s.updated ?? 0,
      tone: 'updated',
      records: d.updated ?? [],
    },
    {
      key: 'unchanged',
      label: t(`${P}.unchanged`),
      count: s.unchanged ?? 0,
      tone: 'muted',
      records: d.unchanged ?? [],
    },
    {
      key: 'skipped',
      label: t(`${P}.skipped`),
      count: s.skipped ?? 0,
      tone: 'warning',
      records: d.skipped ?? [],
    },
    {
      key: 'inactive',
      label: t(`${P}.inactive`),
      count: s.inactive ?? 0,
      tone: 'muted',
      records: d.inactive ?? [],
    },
    {
      key: 'excluded',
      label: t(`${P}.excluded`),
      count: s.excluded ?? 0,
      tone: 'muted',
      records: d.excluded ?? [],
    },
  ];

  // Wysyłanie żądania albo zadanie w toku — tło i Esc nie zamykają okna (X i „Zamknij" działają).
  const running =
    startSyncMutation.isPending ||
    (stage === 'progress' && isJobInFlight(job, timedOut, jobQ.isError));

  return (
    <Modal
      open
      onClose={onClose}
      title={t('tenant.settings.integrations.syncEmployees.title', { name: integrationName })}
      size="lg"
      expandable
      expanded={expanded}
      onExpandedChange={setExpanded}
      testId="sync-employees-modal"
      closeOnBackdrop={!running}
      closeOnEsc={!running}
      footer={
        <>
          {stage === 'choose' && (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => startSyncMutation.mutate()}
                disabled={startSyncMutation.isPending || (mode === 'incremental' && !dateFrom)}
                data-testid="sync-employees-start-btn"
              >
                {startSyncMutation.isPending
                  ? t('tenant.settings.integrations.syncEmployees.starting')
                  : t('tenant.settings.integrations.syncEmployees.startButton')}
              </Button>
            </>
          )}
          {stage === 'progress' && (
            <Button variant="primary" onClick={onClose} data-testid="sync-employees-close-btn">
              {t('common.close')}
            </Button>
          )}
        </>
      }
    >
      {stage === 'choose' && (
        <div className="space-y-4" data-testid="sync-employees-stage-choose">
          <p className="text-sm text-text-secondary">
            {t('tenant.settings.integrations.syncEmployees.chooseHint')}
          </p>
          <RadioGroup<Mode>
            name="sync-employees-mode"
            legend={t('tenant.settings.integrations.syncEmployees.modeLegend')}
            value={mode}
            onChange={setMode}
            options={[
              {
                value: 'full',
                label: t('tenant.settings.integrations.syncEmployees.modeFullTitle'),
                description: t('tenant.settings.integrations.syncEmployees.modeFullHint'),
              },
              {
                value: 'incremental',
                label: t('tenant.settings.integrations.syncEmployees.modeIncrementalTitle'),
                description: t('tenant.settings.integrations.syncEmployees.modeIncrementalHint'),
              },
            ]}
          />
          {mode === 'incremental' && (
            <Input
              type="date"
              label={t('tenant.settings.integrations.syncEmployees.dateFromLabel')}
              value={dateFrom}
              max={todayISO()}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="sync-employees-date-from"
            />
          )}

          {startSyncMutation.isError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-400">
              {t('tenant.settings.integrations.syncEmployees.startError')}
            </div>
          )}
        </div>
      )}

      {stage === 'progress' && (
        <div
          className="flex h-full min-h-0 flex-col gap-4"
          data-testid={`sync-employees-status-${
            job?.status === 'completed' || job?.status === 'failed'
              ? job.status
              : timedOut
                ? 'timeout'
                : (job?.status ?? 'pending')
          }`}
        >
          {!timedOut && (!job || job.status === 'pending' || job.status === 'running') && (
            <div className="flex items-center gap-3 rounded-md bg-bg-secondary p-3">
              <Loader2 className="h-5 w-5 animate-spin text-accent-500" aria-hidden="true" />
              <div>
                <div className="font-medium text-text-primary">
                  {job?.status === 'running'
                    ? t('tenant.settings.integrations.syncEmployees.running')
                    : t('tenant.settings.integrations.syncEmployees.waitingForAgent')}
                </div>
                <div className="text-xs text-text-tertiary">
                  {t('tenant.settings.integrations.syncEmployees.runningHint')}
                </div>
              </div>
            </div>
          )}

          {timedOut && job?.status !== 'completed' && job?.status !== 'failed' && (
            <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
              <Clock className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" aria-hidden="true" />
              <div>
                <div className="font-medium text-red-600 dark:text-red-400">
                  {t('tenant.settings.integrations.syncEmployees.timeoutTitle')}
                </div>
                <div className="text-xs text-text-secondary">
                  {t('tenant.settings.integrations.syncEmployees.timeoutHint')}
                </div>
              </div>
            </div>
          )}

          {job?.status === 'completed' && (
            <div className="flex min-h-0 flex-1 flex-col gap-3">
              <div className="flex shrink-0 items-start gap-3 rounded-md border border-green-500/30 bg-green-500/10 p-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" aria-hidden="true" />
                <div>
                  <div className="font-medium text-green-700 dark:text-green-400">
                    {t('tenant.settings.integrations.syncEmployees.successTitle')}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {t('tenant.settings.integrations.syncEmployees.successHint')}
                  </div>
                </div>
              </div>

              <SyncResultPanel
                tiles={resultTiles}
                onNavigate={onClose}
                linkBase="/employees"
                testId="import-employees-result"
                fillHeight={expanded}
                listHeightClass="h-72"
              />
            </div>
          )}

          {job?.status === 'failed' && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
                <AlertCircle
                  className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400"
                  aria-hidden="true"
                />
                <div>
                  <div className="font-medium text-red-600 dark:text-red-400">
                    {t('tenant.settings.integrations.syncEmployees.failedTitle')}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {t('tenant.settings.integrations.syncEmployees.failedHint')}
                  </div>
                </div>
              </div>

              {job.result && (
                <pre className="max-h-64 overflow-auto rounded-md border border-red-500/30 bg-red-500/5 p-3 font-mono text-xs text-red-700 dark:text-red-400">
                  {JSON.stringify(job.result, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
